import type { Case } from '../types/case';
import { formatCurrency, formatDate } from '../lib/format';

interface Props {
  case: Case;
}

/** Detail keys with first-class rendering. Anything NOT listed here still
    renders via the generic fallback — a captured field must never be
    invisible to the mediator (Morgan's empty Situation card, 2026-08-13). */
const CURRENCY_KEYS = new Set(['total_owed', 'monthly_rent', 'fees_accrued', 'claim_amount', 'last_payment_amount']);
const DATE_KEYS = new Set(['last_payment_date', 'lease_start', 'lease_end']);
const NARRATIVE_KEYS = new Set(['dispute_description', 'dispute_summary']);
const HIDDEN_KEYS = new Set(['balance_breakdown']); // internal/empty structures

const LABELS: Record<string, string> = {
  total_owed: 'Total owed',
  monthly_rent: 'Monthly rent',
  months_delinquent: 'Months behind',
  fees_accrued: 'Fees accrued',
  last_payment_date: 'Last payment',
  last_payment_amount: 'Last payment amount',
  lease_start: 'Lease starts',
  lease_end: 'Lease ends',
  claim_amount: 'Claim amount',
};

const prettify = (key: string) =>
  key.replace(/_/g, ' ').replace(/^\w/, (ch) => ch.toUpperCase());

function renderValue(key: string, value: unknown): React.ReactNode {
  if (CURRENCY_KEYS.has(key) && typeof value === 'number') return formatCurrency(value);
  if (DATE_KEYS.has(key) && typeof value === 'string') return formatDate(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function SituationSection({ case: c }: Props) {
  const partyName = (partyId: string | null) =>
    c.parties.find((p) => p.id === partyId)?.name ?? 'A party';

  // Free-text accounts of the dispute (court intake description, import
  // summary) — the mediator's fastest orientation.
  const narratives = c.claims.flatMap((cl) =>
    Object.entries(cl.details ?? {})
      .filter(([k, v]) => NARRATIVE_KEYS.has(k) && typeof v === 'string' && v)
      .map(([, v]) => ({ claim: cl, text: String(v) })),
  );

  // Every structured detail across every claim, deduped by key.
  const rows: Array<{ key: string; value: unknown }> = [];
  const seen = new Set<string>();
  for (const cl of c.claims) {
    for (const [key, value] of Object.entries(cl.details ?? {})) {
      if (seen.has(key) || NARRATIVE_KEYS.has(key) || HIDDEN_KEYS.has(key)) continue;
      if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) continue;
      seen.add(key);
      rows.push({ key, value });
    }
  }

  // Case-level facts mediators need at a glance.
  const meta = (c.metadata ?? {}) as Record<string, unknown>;
  const courtCaseNo = typeof meta.court_case_no === 'string' ? meta.court_case_no : null;
  const consents = (meta.a2m_consents ?? {}) as Record<string, string>;
  const schedulable = c.parties.filter((p) => ['claimant', 'respondent'].includes(p.role_archetype));

  return (
    <section className="card p-4">
      <div className="section-label">Situation</div>
      {narratives.map((n, i) => (
        <div key={i} className="mt-2">
          <div className="text-xs font-medium text-gray-500">
            What the dispute is about{n.claim.party_id ? ` — ${partyName(n.claim.party_id)}` : ''}
          </div>
          <p className="mt-0.5 whitespace-pre-wrap text-sm text-gray-800">{n.text}</p>
        </div>
      ))}
      <dl className="mt-2 space-y-1.5 text-sm">
        {courtCaseNo && <Row label="Court case no.">{courtCaseNo}</Row>}
        {c.trial_date && <Row label="Trial date"><span className="font-semibold">{formatDate(c.trial_date)}</span></Row>}
        {rows.map(({ key, value }) => (
          <Row key={key} label={LABELS[key] ?? prettify(key)}>
            {renderValue(key, value)}
          </Row>
        ))}
        {schedulable.length > 0 && Object.keys(consents).length > 0 && (
          <Row label="Consent to mediate">
            <div className="space-y-0.5 text-right">
              {schedulable.map((p) => (
                <div key={p.id}>
                  {p.name.split(' ')[0]}{' '}
                  {consents[p.id] ? (
                    <span className="text-green-700">✓ {formatDate(consents[p.id])}</span>
                  ) : (
                    <span className="text-amber-700">pending</span>
                  )}
                </div>
              ))}
            </div>
          </Row>
        )}
        {c.tags.length > 0 && (
          <Row label="Tags">
            <div className="flex flex-wrap gap-1">
              {c.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-surface-muted px-2 py-0.5 text-xs text-gray-700">
                  {tag}
                </span>
              ))}
            </div>
          </Row>
        )}
      </dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}
